// D:\seat-reservation-react-v2\src\services\chatService.js
import axios from "axios";
import io from "socket.io-client";

const API_URL =
  process.env.REACT_APP_API_URL || "https://27ebedc8342d.ngrok-free.app/api/v2";

// Fixed getToken function
const getToken = () => {
  try {
    const persistUser = localStorage.getItem("persist:user");
    if (persistUser) {
      const parsedPersistUser = JSON.parse(persistUser);
      if (parsedPersistUser.user) {
        try {
          const userData = JSON.parse(parsedPersistUser.user);
          return userData.token;
        } catch (parseError) {
          console.error("Error parsing user data from persist:", parseError);
        }
      }
    }
    return localStorage.getItem("token") || sessionStorage.getItem("token");
  } catch (error) {
    console.error("Error getting token:", error);
    return null;
  }
};

// Get auth user data
export const getAuthUser = () => {
  try {
    const persistUser = localStorage.getItem("persist:user");
    if (persistUser) {
      const parsedPersistUser = JSON.parse(persistUser);
      if (parsedPersistUser.user) {
        return JSON.parse(parsedPersistUser.user);
      }
    }
    return null;
  } catch (error) {
    console.error("Error getting auth user:", error);
    return null;
  }
};

// Create axios instance
const getAxiosInstance = () => {
  const token = getToken();

  return axios.create({
    baseURL: API_URL,
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
    timeout: 10000,
  });
};

// ==================== DIRECT MESSAGING ====================

export const startDirectChat = async (otherUserId) => {
  try {
    const response = await getAxiosInstance().post("/chat/direct/start", {
      otherUserId,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const sendDirectMessage = async (data) => {
  try {
    const response = await getAxiosInstance().post("/chat/direct/send", data);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getDirectConversations = async () => {
  try {
    const response = await getAxiosInstance().get("/chat/direct/conversations");
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getDirectMessages = async (
  otherUserId,
  limit = 100,
  offset = 0,
) => {
  try {
    const response = await getAxiosInstance().get(
      `/chat/direct/messages/${otherUserId}`,
      {
        params: { limit, offset },
      },
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const deleteAllDirectMessages = async (otherUserId) => {
  try {
    const response = await getAxiosInstance().delete(
      `/chat/direct/delete-all/${otherUserId}`,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const markAllDirectMessagesRead = async (otherUserId) => {
  try {
    const response = await getAxiosInstance().post(
      `/chat/direct/mark-all-read/${otherUserId}`,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const clearConversationHistory = async (otherUserId) => {
  try {
    const response = await getAxiosInstance().post(
      `/chat/direct/clear-history/${otherUserId}`,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const deleteConversationForUser = async (otherUserId) => {
  try {
    const response = await getAxiosInstance().delete(
      `/chat/direct/conversation/${otherUserId}`,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// ==================== GROUP CHAT ====================

export const createGroupChat = async (data) => {
  try {
    const response = await getAxiosInstance().post("/chat/group/create", data);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const sendGroupMessage = async (data) => {
  try {
    const response = await getAxiosInstance().post("/chat/group/send", data);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getUserGroups = async () => {
  try {
    const response = await getAxiosInstance().get("/chat/group/list");
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Update the getGroupMessages function:

export const getGroupMessages = async (groupId, limit = 100, offset = 0) => {
  try {
    console.log(`🔄 Fetching group messages for group ${groupId}`);
    const response = await getAxiosInstance().get(
      `/chat/group/messages/${groupId}`,
      {
        params: { limit, offset },
      },
    );

    console.log(`✅ Group messages API response for group ${groupId}:`, {
      success: response.data.success,
      messageCount: response.data.data?.messages?.length || 0,
    });

    if (response.data.success && response.data.data?.messages) {
      console.log(
        `📝 Sample messages:`,
        response.data.data.messages.slice(0, 3),
      );
    }

    return response.data;
  } catch (error) {
    console.error(
      `❌ Error fetching group messages for group ${groupId}:`,
      error,
    );
    throw error.response?.data || error;
  }
};
export const getAllGroupMessages = async (groupId) => {
  try {
    const response = await getAxiosInstance().get(
      `/chat/group/all-messages/${groupId}`,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const deleteAllGroupMessages = async (groupId) => {
  try {
    const response = await getAxiosInstance().delete(
      `/chat/group/delete-all/${groupId}`,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const clearGroupHistory = async (groupId) => {
  try {
    const response = await getAxiosInstance().post(
      `/chat/group/clear-history/${groupId}`,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const addGroupMembers = async (groupId, participantIds) => {
  try {
    const response = await getAxiosInstance().post(
      `/chat/group/${groupId}/members`,
      {
        participantIds,
      },
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getUsersForGroup = async (groupId, search = "") => {
  try {
    const response = await getAxiosInstance().get(
      `/chat/group/${groupId}/available-users`,
      {
        params: { search },
      },
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const updateGroup = async (groupId, data) => {
  try {
    const response = await getAxiosInstance().put(
      `/chat/group/${groupId}`,
      data,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const leaveGroup = async (groupId) => {
  try {
    const response = await getAxiosInstance().post(
      `/chat/group/${groupId}/leave`,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const deleteGroup = async (groupId) => {
  try {
    const response = await getAxiosInstance().delete(`/chat/group/${groupId}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const removeGroupMember = async (groupId, memberId) => {
  try {
    const response = await getAxiosInstance().delete(
      `/chat/group/${groupId}/member/${memberId}`,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getGroupDetails = async (groupId) => {
  try {
    const response = await getAxiosInstance().get(
      `/chat/group/${groupId}/details`,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// ==================== USER MANAGEMENT ====================

export const getUsersForChat = async (search = "", role = "") => {
  try {
    const params = {};
    if (search) params.search = search;
    if (role) params.role = role;

    const response = await getAxiosInstance().get("/chat/users", {
      params,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getUsersByRole = async (role) => {
  try {
    const response = await getAxiosInstance().get("/chat/users", {
      params: { role },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// ==================== BLOCK MANAGEMENT ====================

export const blockUser = async (blockedId, reason) => {
  try {
    const response = await getAxiosInstance().post("/chat/block", {
      blockedId,
      reason,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const unblockUser = async (blockedId) => {
  try {
    const response = await getAxiosInstance().post("/chat/unblock", {
      blockedId,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getBlockedUsers = async () => {
  try {
    const response = await getAxiosInstance().get("/chat/blocked");
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// ==================== MESSAGE MANAGEMENT ====================

export const deleteMessage = async (messageId, deleteForEveryone = false) => {
  try {
    const response = await getAxiosInstance().delete(
      `/chat/message/${messageId}`,
      {
        data: { deleteForEveryone },
      },
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const deleteMultipleMessages = async (
  messageIds,
  deleteForEveryone = false,
) => {
  try {
    const response = await getAxiosInstance().post(
      "/chat/messages/delete-multiple",
      {
        messageIds,
        deleteForEveryone,
      },
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const markMessagesAsRead = async (messageIds) => {
  try {
    const response = await getAxiosInstance().post("/chat/messages/read", {
      messageIds,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const markAllMessagesAsRead = async (conversationId, chatType) => {
  try {
    const response = await getAxiosInstance().post(
      "/chat/messages/mark-all-read",
      {
        conversationId,
        chatType,
      },
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// ==================== STATUS ====================

export const getUnreadCounts = async () => {
  try {
    const response = await getAxiosInstance().get("/chat/unread-counts");
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// ==================== SOCKET.IO ====================

let socketInstance = null;

export const initSocketConnection = () => {
  if (socketInstance && socketInstance.connected) {
    console.log("✅ Socket already connected");
    return socketInstance;
  }

  const token = getToken();
  if (!token) {
    console.error("No token found for Socket.IO connection");
    return null;
  }

  console.log("🔌 Initializing Socket.IO connection...");

  socketInstance = io(
    process.env.REACT_APP_SERVER_URL || "https://api.v2.aeon-app.com:9444",
    {
      auth: { token },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      timeout: 20000,
      path: "/socket.io/",
    },
  );

  // Socket event listeners
  socketInstance.on("connect", () => {
    console.log("✅ Connected to chat server");
  });

  socketInstance.on("disconnect", (reason) => {
    console.log("🔌 Socket disconnected:", reason);
  });

  socketInstance.on("connect_error", (error) => {
    console.error("❌ Socket connection error:", error);
  });

  return socketInstance;
};

export const disconnectSocket = () => {
  if (socketInstance) {
    console.log("🔌 Disconnecting socket...");
    socketInstance.disconnect();
    socketInstance = null;
  }
};

export const getSocketInstance = () => {
  return socketInstance;
};

export const isSocketConnected = () => {
  return socketInstance && socketInstance.connected;
};

// ==================== UTILITY FUNCTIONS (REMOVED LOCALSTORAGE) ====================

// Chat state management (minimal - only for UI persistence)
export const saveChatState = (activeConversation, chatType, activeTab) => {
  try {
    const state = {
      activeConversation,
      chatType,
      activeTab,
      timestamp: Date.now(),
    };
    sessionStorage.setItem("chatState", JSON.stringify(state));
  } catch (error) {
    console.error("Error saving chat state:", error);
  }
};

export const loadChatState = () => {
  try {
    const savedState = sessionStorage.getItem("chatState");
    if (savedState) {
      return JSON.parse(savedState);
    }
  } catch (error) {
    console.error("Error loading chat state:", error);
  }
  return {
    activeConversation: null,
    chatType: "direct",
    activeTab: "direct",
  };
};

export const clearChatState = () => {
  try {
    sessionStorage.removeItem("chatState");
  } catch (error) {
    console.error("Error clearing chat state:", error);
  }
};

// Export all functions
export default {
  // Direct messaging
  startDirectChat,
  sendDirectMessage,
  getDirectConversations,
  getDirectMessages,
  deleteAllDirectMessages,
  markAllDirectMessagesRead,
  clearConversationHistory,
  deleteConversationForUser,

  // Group chat
  createGroupChat,
  sendGroupMessage,
  getUserGroups,
  getGroupMessages,
  getAllGroupMessages,
  deleteAllGroupMessages,
  clearGroupHistory,
  addGroupMembers,
  getUsersForGroup,
  updateGroup,
  leaveGroup,
  deleteGroup,
  removeGroupMember,
  getGroupDetails,

  // User management
  getUsersForChat,
  getUsersByRole,

  // Block management
  blockUser,
  unblockUser,
  getBlockedUsers,

  // Message management
  deleteMessage,
  deleteMultipleMessages,
  markMessagesAsRead,
  markAllMessagesAsRead,

  // Status
  getUnreadCounts,

  // Socket
  initSocketConnection,
  disconnectSocket,
  getSocketInstance,
  isSocketConnected,

  // Auth
  getAuthUser,

  // Utility
  saveChatState,
  loadChatState,
  clearChatState,
};
